import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchWithRetry } from '@/lib/apiRequest';

export interface Lead {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  source: string | null;
  legalProductId: string | null;
  legalProductName: string | null;
  caseDescription: string | null;
  status: string;
  priority: string;
  responsibleUser: string | null;
  estimatedValue: number | null;
  nextContactAt: string | null;
  lastContactAt: string | null;
  convertedClientId: string | null;
  convertedCaseId: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateLeadInput {
  name: string;
  phone?: string | null;
  email?: string | null;
  source?: string | null;
  legalProductId?: string | null;
  caseDescription?: string | null;
  status?: string;
  priority?: string;
  responsibleUser?: string | null;
  estimatedValue?: number | null;
  nextContactAt?: string | null;
  lastContactAt?: string | null;
  convertedClientId?: string | null;
  convertedCaseId?: string | null;
  notes?: string | null;
}

export type UpdateLeadInput = CreateLeadInput;

export interface LeadLoadFilters {
  status?: string;
  legalProductId?: string;
}

type UseLeadsOptions = {
  autoLoad?: boolean;
};

const allowedStatuses = new Set(['new', 'contacted', 'proposal_sent', 'hired', 'lost', 'archived']);
const allowedPriorities = new Set(['low', 'normal', 'high', 'urgent']);

const legacyStatusMap: Record<string, string> = {
  Novo: 'new',
  Triagem: 'contacted',
  'Aguardando documentos': 'contacted',
  'Análise paga': 'contacted',
  'Proposta enviada': 'proposal_sent',
  'Contrato enviado': 'proposal_sent',
  Pago: 'hired',
  'Caso ativo': 'hired',
  Perdido: 'lost',
};

const legacyPriorityMap: Record<string, string> = {
  Baixa: 'low',
  Normal: 'normal',
  Alta: 'high',
  Urgente: 'urgent',
};

const toNullableText = (value: unknown) => {
  if (value === undefined || value === null) {
    return null;
  }

  const text = String(value).trim();
  return text === '' ? null : text;
};

const toNullableNumber = (value: unknown) => {
  if (value === undefined || value === null || value === '') {
    return null;
  }

  const number = Number(String(value).replace(',', '.'));
  return Number.isFinite(number) ? number : null;
};

const toLeadStatus = (value: unknown) => {
  const text = toNullableText(value);
  if (!text) {
    return 'new';
  }

  const normalized = text.toLowerCase();
  if (allowedStatuses.has(normalized)) {
    return normalized;
  }

  return legacyStatusMap[text] ?? 'new';
};

const toLeadPriority = (value: unknown) => {
  const text = toNullableText(value);
  if (!text) {
    return 'normal';
  }

  const normalized = text.toLowerCase();
  if (allowedPriorities.has(normalized)) {
    return normalized;
  }

  return legacyPriorityMap[text] ?? 'normal';
};

const sortLeadsByCreatedAtDesc = (a: Lead, b: Lead) => {
  const left = Date.parse(a.createdAt);
  const right = Date.parse(b.createdAt);

  if (Number.isFinite(left) && Number.isFinite(right)) {
    return right - left;
  }

  return b.id.localeCompare(a.id);
};

const normalizeLead = (value: any): Lead => ({
  id: String(value?.id ?? ''),
  name: String(value?.name ?? '').trim(),
  phone: toNullableText(value?.phone),
  email: toNullableText(value?.email),
  source: toNullableText(value?.source),
  legalProductId: toNullableText(value?.legalProductId ?? value?.legal_product_id),
  legalProductName: toNullableText(value?.legalProductName ?? value?.legal_product_name),
  caseDescription: toNullableText(value?.caseDescription ?? value?.case_description),
  status: toLeadStatus(value?.status),
  priority: toLeadPriority(value?.priority),
  responsibleUser: toNullableText(value?.responsibleUser ?? value?.responsible_user),
  estimatedValue: toNullableNumber(value?.estimatedValue ?? value?.estimated_value),
  nextContactAt: toNullableText(value?.nextContactAt ?? value?.next_contact_at),
  lastContactAt: toNullableText(value?.lastContactAt ?? value?.last_contact_at),
  convertedClientId: toNullableText(value?.convertedClientId ?? value?.converted_client_id),
  convertedCaseId: toNullableText(value?.convertedCaseId ?? value?.converted_case_id),
  notes: toNullableText(value?.notes),
  createdAt: toNullableText(value?.createdAt ?? value?.created_at) ?? new Date().toISOString(),
  updatedAt: toNullableText(value?.updatedAt ?? value?.updated_at) ?? new Date().toISOString(),
});

export function useLeads(options: UseLeadsOptions = {}) {
  const { autoLoad = true } = options;
  const [leads, setLeads] = useState<Lead[]>([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(true);
  const [leadsError, setLeadsError] = useState<string | null>(null);
  const requestIdRef = useRef(0);

  const loadLeads = useCallback(async (filters: LeadLoadFilters = {}) => {
    const requestId = ++requestIdRef.current;
    setIsLoadingLeads(true);
    setLeadsError(null);

    try {
      const searchParams = new URLSearchParams();

      if (filters.status?.trim()) {
        searchParams.set('status', filters.status.trim());
      }

      if (filters.legalProductId?.trim()) {
        searchParams.set('legalProductId', filters.legalProductId.trim());
      }

      const endpoint = searchParams.toString() ? `/api/leads?${searchParams.toString()}` : '/api/leads';
      const response = await fetchWithRetry(endpoint);
      const data = await response.json();

      if (!response.ok || !data.success || !Array.isArray(data.leads)) {
        throw new Error(data.error || `GET /api/leads falhou com status ${response.status}`);
      }

      if (requestId === requestIdRef.current) {
        setLeads(data.leads.map(normalizeLead).sort(sortLeadsByCreatedAtDesc));
      }
    } catch (error) {
      console.error('Erro ao carregar leads reais. ', error);

      if (requestId === requestIdRef.current) {
        setLeads([]);
        setLeadsError('Não foi possível carregar os dados reais. Tente novamente.');
      }
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoadingLeads(false);
      }
    }
  }, []);

  const createLead = useCallback(async (input: CreateLeadInput) => {
    const response = await fetch('/api/leads', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.lead) {
      throw new Error(data.error || `POST /api/leads falhou com status ${response.status}`);
    }

    const created = normalizeLead(data.lead);
    setLeads((prev) => [created, ...prev.filter((lead) => lead.id !== created.id)]);
    return created;
  }, []);

  const updateLead = useCallback(async (id: string, input: UpdateLeadInput) => {
    const response = await fetch(`/api/leads/${encodeURIComponent(id)}`, {
      method: 'PUT',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });

    const data = await response.json();

    if (!response.ok || !data.success || !data.lead) {
      throw new Error(data.error || `PUT /api/leads/${id} falhou com status ${response.status}`);
    }

    const updated = normalizeLead(data.lead);
    setLeads((prev) => prev.map((lead) => (lead.id === updated.id ? updated : lead)));
    return updated;
  }, []);

  const deleteLead = useCallback(async (id: string) => {
    const response = await fetch(`/api/leads/${encodeURIComponent(id)}`, {
      method: 'DELETE',
    });

    const data = await response.json();

    if (!response.ok || !data.success) {
      throw new Error(data.error || `DELETE /api/leads/${id} falhou com status ${response.status}`);
    }

    setLeads((prev) =>
      prev.map((lead) =>
        lead.id === id
          ? {
              ...lead,
              status: 'archived',
              updatedAt: new Date().toISOString(),
            }
          : lead
      )
    );

    return true;
  }, []);

  useEffect(() => {
    if (!autoLoad) {
      return;
    }

    void loadLeads();
  }, [autoLoad, loadLeads]);

  return {
    leads,
    isLoadingLeads,
    leadsError,
    loadLeads,
    createLead,
    updateLead,
    deleteLead,
  };
}
